import { d as debounce } from "@theme/utils";
class HeaderSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector(".header-search-input");
    this.form = this.querySelector("form");
    this.results = this.querySelector(".header-search-results");
    this.loading = this.querySelector(".header-search-loading");
    this.noResults = this.querySelector(".header-search-no-results");
    this.content = this.querySelector(".header-search-content");
    this.productsGrid = this.querySelector(".header-search-products");
    this.queriesContainer = this.querySelector(".header-search-queries");
    this.viewAllLink = this.querySelector(".header-search-view-all");
    this.collectionsContainer = this.querySelector(".header-search-collections");
    this.suggestionsContainer = this.querySelector(".header-search-suggestions");
    this.noResultsMessage = this.querySelector(
      ".header-search-no-results-message"
    );
    this.noResultsSuggestions = this.querySelector(
      ".header-search-no-results-suggestions"
    );
    this.currentQuery = "";
    this.suggestions = window.search_suggestions || null;
    if (this.suggestions && this.suggestions.items?.length > 0) {
      const suggestionsHtml = `
        <div class="mb-3 text-sm font-semibold text-secondary-dark uppercase">${this.suggestions.title}</div>
        <ul class="flex flex-col gap-1.5">
          ${this.suggestions.items.map(
        (text) => `
            <li>
              <button type="button" data-suggestion="${text}" class="flex w-full cursor-pointer items-center gap-2 rounded-lg border border-gray-border px-4 py-2 text-left text-sm text-black transition-colors hover:bg-gray-100"><svg class="h-3.5 w-3.5 shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>${text}</button>
            </li>
          `
      ).join("")}
        </ul>
      `;
      this.suggestionsContainer.innerHTML = suggestionsHtml;
      this.noResultsSuggestions.innerHTML = `<div class="border-gray-border border-t px-5 py-4">${suggestionsHtml}</div>`;
      const handleSuggestionClick = (e) => {
        const btn = e.target.closest("[data-suggestion]");
        if (!btn) return;
        this.input.value = btn.dataset.suggestion;
        this.handleSearch(btn.dataset.suggestion);
      };
      this.suggestionsContainer.addEventListener("click", handleSuggestionClick);
      this.noResultsSuggestions.addEventListener("click", handleSuggestionClick);
    }
    if (!this.input) return;
    this.input.addEventListener(
      "input",
      debounce((e) => this.handleSearch(e.target.value), 300)
    );
    this.input.addEventListener("focus", () => {
      if (this.currentQuery) this.showResults();
    });
    this.addEventListener("click", (e) => {
      if (e.target.closest(".header-search-products a") || e.target.closest(".header-search-view-all")) {
        this.hideResults();
      }
    });
    document.addEventListener("click", (e) => {
      if (!this.contains(e.target)) this.hideResults();
    });
    this.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        this.hideResults();
        this.input.blur();
      }
    });
    this.form?.addEventListener("submit", (e) => {
      e.preventDefault();
      if (this.currentQuery) {
        const q = this.buildSearchQuery(this.currentQuery);
        window.location.href = `${window.routes.search_url}?q=${encodeURIComponent(q)}&type=product&options[prefix]=last&options[unavailable_products]=show`;
      }
    });
  }
  async handleSearch(query) {
    this.currentQuery = query.trim();
    if (!this.currentQuery) {
      this.hideResults();
      return;
    }
    this.showLoading();
    try {
      const searchUrl = (q) => `/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product,query,collection&resources[limit]=6&resources[limit_scope]=each&resources[options][unavailable_products]=show&resources[options][fields]=title,product_type,tag,vendor,variants.sku`;
      const transliterated = this.transliterate(this.currentQuery);
      const fetches = [fetch(searchUrl(this.currentQuery))];
      if (transliterated !== this.currentQuery) {
        fetches.push(fetch(searchUrl(transliterated)));
      }
      const responses = await Promise.all(fetches);
      const results = await Promise.all(
        responses.map((r) => {
          if (!r.ok) throw new Error("Search request failed");
          return r.json();
        })
      );
      const data = results.length > 1 ? this.mergeResults(results[0], results[1]) : results[0];
      this.renderResults(data);
    } catch (error) {
      console.error("Search error:", error);
      this.showNoResults();
    }
  }
  renderResults(data) {
    const products = data.resources?.results?.products || [];
    const queries = data.resources?.results?.queries || [];
    const collections = data.resources?.results?.collections || [];
    if (this.viewAllLink) {
      const q = this.buildSearchQuery(this.currentQuery);
      this.viewAllLink.href = `${window.routes.search_url}?q=${encodeURIComponent(q)}&type=product&options[prefix]=last&options[unavailable_products]=show`;
    }
    if (products.length === 0 && queries.length === 0 && collections.length === 0) {
      this.showNoResults();
      return;
    }
    if (this.queriesContainer) {
      if (queries.length > 0) {
        this.queriesContainer.innerHTML = `
          <div class="flex flex-wrap gap-2 px-5 pt-4">
            ${queries.map(
          (q) => `
              <a
                href="${window.routes.search_url}?q=${encodeURIComponent(q.text)}&type=product&options[unavailable_products]=show"
                class="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-700 transition hover:bg-gray-200"
              >${q.styled_text}</a>
            `
        ).join("")}
          </div>
        `;
      } else {
        this.queriesContainer.innerHTML = "";
      }
    }
    if (this.productsGrid) {
      this.productsGrid.innerHTML = products.map((product) => {
        const priceEur = this.parsePrice(product.price_min || product.price);
        let compareHtml = "";
        const comparePrice = this.parsePrice(product.compare_at_price_min);
        if (comparePrice && comparePrice > priceEur) {
          compareHtml = `
              <span class="text-xs text-gray-400 line-through">
                €${comparePrice.toFixed(2)}
              </span>
            `;
        }
        return `
          <a href="${product.url}" class="flex items-center gap-3 px-5 py-3 transition hover:bg-gray-50">
            <div class="h-12 w-12 shrink-0 overflow-hidden rounded bg-gray-100">
              ${product.image ? `<img src="${product.image}&width=100" alt="${product.title}" loading="lazy" class="h-full w-full object-cover">` : ""}
            </div>
            <div class="min-w-0 flex-1">
              <div class="truncate text-sm font-medium">${product.title}</div>
              <div class="mt-0.5 flex flex-wrap items-baseline gap-1.5">
                <span class="text-sm font-semibold">€${priceEur.toFixed(2)}</span>
                ${compareHtml}
              </div>
            </div>
          </a>
        `;
      }).join("");
    }
    if (this.collectionsContainer) {
      if (collections.length > 0) {
        this.collectionsContainer.innerHTML = `
          <div class="border-gray-border border-t px-5 py-3">
            <div class="mb-2 text-xs font-semibold text-gray-400 uppercase">${document.documentElement.lang === "bg" ? "Категории" : "Collections"}</div>
            <div class="grid grid-cols-2 gap-2">
              ${collections.slice(0, 6).map(
          (c) => `
                <a href="${c.url}" class="flex items-center gap-2 rounded-lg p-2 transition hover:bg-gray-50">
                  <div class="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded bg-gray-100">
                    ${c.featured_image?.url ? `<img src="${c.featured_image.url}&width=80" alt="${c.title}" loading="lazy" class="h-full w-full object-cover">` : '<svg class="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"/></svg>'}
                  </div>
                  <span class="truncate text-sm font-medium">${c.title}</span>
                </a>
              `
        ).join("")}
            </div>
          </div>
        `;
      } else {
        this.collectionsContainer.innerHTML = "";
      }
    }
    this.showContent();
  }
  parsePrice(priceStr) {
    if (typeof priceStr === "number") return priceStr;
    if (!priceStr) return 0;
    return parseFloat(
      String(priceStr).replace(/[^0-9.,]/g, "").replace(",", ".")
    ) || 0;
  }
  showLoading() {
    this.results?.classList.remove("hidden");
    this.loading?.classList.remove("hidden");
    this.noResults?.classList.add("hidden");
    this.content?.classList.add("hidden");
  }
  showNoResults() {
    this.results?.classList.remove("hidden");
    this.loading?.classList.add("hidden");
    this.noResults?.classList.remove("hidden");
    this.content?.classList.add("hidden");
    if (this.noResultsMessage) {
      const lang = document.documentElement.lang;
      const msg = lang === "bg" ? `Вашето търсене за "<strong>${this.currentQuery}</strong>" не даде резултати.` : `Your search for "<strong>${this.currentQuery}</strong>" did not yield any results.`;
      this.noResultsMessage.innerHTML = msg;
    }
  }
  showContent() {
    this.results?.classList.remove("hidden");
    this.loading?.classList.add("hidden");
    this.noResults?.classList.add("hidden");
    this.content?.classList.remove("hidden");
    if (this.suggestions?.items?.length > 0) {
      this.suggestionsContainer?.classList.remove("hidden");
    }
  }
  showResults() {
    this.results?.classList.remove("hidden");
  }
  hideResults() {
    this.results?.classList.add("hidden");
  }
  buildSearchQuery(query) {
    const trimmed = query.trim();
    const isCyrillic = /[а-яА-Я]/.test(trimmed);
    if (isCyrillic && trimmed.length <= 3) {
      return `${trimmed}*`;
    }
    return trimmed;
  }
  transliterate(text) {
    const lower = text.toLowerCase();
    const isCyrillic = /[а-яА-Я]/.test(text);
    if (isCyrillic) {
      const map2 = [
        ["щ", "sht"],
        ["ш", "sh"],
        ["ч", "ch"],
        ["ж", "zh"],
        ["ц", "ts"],
        ["ю", "yu"],
        ["я", "ya"],
        ["а", "a"],
        ["б", "b"],
        ["в", "v"],
        ["г", "g"],
        ["д", "d"],
        ["е", "e"],
        ["з", "z"],
        ["и", "i"],
        ["й", "y"],
        ["к", "k"],
        ["л", "l"],
        ["м", "m"],
        ["н", "n"],
        ["о", "o"],
        ["п", "p"],
        ["р", "r"],
        ["с", "s"],
        ["т", "t"],
        ["у", "u"],
        ["ф", "f"],
        ["х", "h"],
        ["ъ", "a"],
        ["ь", "y"]
      ];
      let result2 = lower;
      for (const [cyr, lat] of map2) {
        result2 = result2.replaceAll(cyr, lat);
      }
      return result2;
    }
    const map = [
      ["sht", "щ"],
      ["sh", "ш"],
      ["ch", "ч"],
      ["zh", "ж"],
      ["ts", "ц"],
      ["yu", "ю"],
      ["ya", "я"],
      ["ia", "я"],
      ["iu", "ю"],
      ["j", "ж"],
      ["4", "ч"],
      ["6", "ш"],
      ["c", "ц"],
      ["a", "а"],
      ["b", "б"],
      ["v", "в"],
      ["g", "г"],
      ["d", "д"],
      ["e", "е"],
      ["z", "з"],
      ["i", "и"],
      ["y", "й"],
      ["k", "к"],
      ["l", "л"],
      ["m", "м"],
      ["n", "н"],
      ["o", "о"],
      ["p", "п"],
      ["r", "р"],
      ["s", "с"],
      ["t", "т"],
      ["u", "у"],
      ["f", "ф"],
      ["h", "х"]
    ];
    let result = lower;
    for (const [lat, cyr] of map) {
      result = result.replaceAll(lat, cyr);
    }
    return result;
  }
  mergeResults(data1, data2) {
    const products1 = data1.resources?.results?.products || [];
    const products2 = data2.resources?.results?.products || [];
    const queries1 = data1.resources?.results?.queries || [];
    const queries2 = data2.resources?.results?.queries || [];
    const collections1 = data1.resources?.results?.collections || [];
    const collections2 = data2.resources?.results?.collections || [];
    const seen = /* @__PURE__ */ new Set();
    const mergedProducts = [];
    for (const p of [...products1, ...products2]) {
      if (!seen.has(p.id)) {
        seen.add(p.id);
        mergedProducts.push(p);
      }
    }
    const seenQueries = /* @__PURE__ */ new Set();
    const mergedQueries = [];
    for (const q of [...queries1, ...queries2]) {
      if (!seenQueries.has(q.text)) {
        seenQueries.add(q.text);
        mergedQueries.push(q);
      }
    }
    const seenCollections = /* @__PURE__ */ new Set();
    const mergedCollections = [];
    for (const c of [...collections1, ...collections2]) {
      if (!seenCollections.has(c.id)) {
        seenCollections.add(c.id);
        mergedCollections.push(c);
      }
    }
    return {
      resources: {
        results: {
          products: mergedProducts,
          queries: mergedQueries,
          collections: mergedCollections
        }
      }
    };
  }
}
customElements.define("header-search", HeaderSearch);
